import moment from 'moment';
import { convertDateToLocal, convertDateToPST } from './audit-trail';
import { TARGET_HELD_ACCESS, SS_FILTER_ACCESS, SS_ORGS_WITHOUT_COTA_AT_FLOOR_ACCESS } from './constants';
import { getDropdownUserOrg } from './user';

const getTimeDiffSegment = date => {
  const current = new Date();
  const now = moment(current);
  const eventTime = moment(date);
  const duration = moment.duration(eventTime.diff(now));
  const minutes = Math.floor(duration.asMinutes());
  const timeDiffSegment = Math.floor(minutes / 30) * 30;
  return timeDiffSegment;
};

const checkIfDateIsPast = date => {
  const current = new Date();
  const now = moment(current);
  const eventTime = moment(date);
  const duration = moment.duration(eventTime.diff(now));
  const minutes = Math.floor(duration.asMinutes());
  // Return true when minutes is less than -60 which mean it has already passed more than 1 hour
  return minutes < -60;
};

export const mutateSalesData = (data, calculateTimeSegment = false) => {
  let lastDatetime = null;
  const refetchTarget = [];
  const markedData = data.map(item => {
    let timeSegment = item.time_diff_segment;
    if (calculateTimeSegment) {
      timeSegment = getTimeDiffSegment(convertDateToLocal(item.datetime));
      const timeSegmentDiff = Math.floor(Math.floor(item.time_diff_segment / 60) - Math.floor(timeSegment / 60));
      // If time segment diff is greater than 1, it means we need to retrigger the calculation for the event held
      if (timeSegmentDiff >= 1) {
        refetchTarget.push(item.lobsang_event_id);
      }
    }
    const isFirst = timeSegment !== lastDatetime;
    lastDatetime = timeSegment;

    // Return a new object with the 'first' property
    return {
      ...item,
      first: isFirst,
      time_diff_segment: calculateTimeSegment ? timeSegment : item.time_diff_segment,
    };
  });
  const filteredData = markedData.reduce((acc, item) => {
    if (!checkIfDateIsPast(convertDateToLocal(item.datetime))) {
      acc.push(item);
    }
    return acc;
  }, []);
  return {
    data: filteredData,
    refetch: refetchTarget,
  };
};

export const salesStreamSorting = (data, sortingType) => {
  const compareByDate = (a, b) => new Date(a.datetime) - new Date(b.datetime);
  const compareByCota = (a, b) => b.cota - a.cota;
  const compareByName = (a, b) => a.name.localeCompare(b.name);

  const sorters = {
    datetime: (a, b) => compareByDate(a, b) || compareByCota(a, b) || compareByName(a, b),
    cota: (a, b) => compareByCota(a, b) || compareByDate(a, b) || compareByName(a, b),
    tickets: (a, b) => b.available_count - a.available_count || compareByCota(a, b) || compareByName(a, b),
    target: (a, b) => b.slowest_target - a.slowest_target || compareByCota(a, b) || compareByName(a, b),
    cota_at_floor: (a, b) => b.cota_at_floor - a.cota_at_floor || compareByCota(a, b) || compareByName(a, b),
  };

  const sortedData = data.sort(sorters[sortingType]);
  return mutateSalesData(sortedData);
};

export const checkIfTargetAvailable = (org = 0) => {
  if (org === 0) {
    const userOrg = getDropdownUserOrg();
    return TARGET_HELD_ACCESS.includes(userOrg);
  }
  return TARGET_HELD_ACCESS.includes(parseInt(org, 10));
};

export const checkIfFilterAvailable = () => {
  const userOrg = getDropdownUserOrg();
  return SS_FILTER_ACCESS.includes(userOrg);
};

export const hasCotaAtFloorAccess = () => {
  const userOrgId = getDropdownUserOrg();
  return !SS_ORGS_WITHOUT_COTA_AT_FLOOR_ACCESS.includes(userOrgId);
};

export const formatLastSaleDate = (lastSaleDate, lastSoldMarketplace) => {
  if (lastSaleDate && lastSoldMarketplace) {
    const pstDate = new Date(convertDateToPST(lastSaleDate));
    let hours = pstDate.getHours();
    const minutes = pstDate.getMinutes();
    const period = hours >= 12 ? 'P.M.' : 'A.M.';
    hours = hours % 12 || 12;
    const formattedSaleTime = `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
    const wordToRemove = '(1Ticket)';
    let marketplace = lastSoldMarketplace;
    if (marketplace.includes(wordToRemove)) {
      marketplace = marketplace.replace(wordToRemove, '');
    }
    return `${marketplace} ${formattedSaleTime}`;
  }
  return '---';
};

export const formatLastPriceChangeDate = lastPriceChange => {
  if (lastPriceChange) {
    const pstDate = new Date(convertDateToPST(lastPriceChange));
    const day = String(pstDate.getDate()).padStart(2, '0');
    const month = String(pstDate.getMonth() + 1).padStart(2, '0');
    const year = pstDate.getFullYear();
    let hours = pstDate.getHours();
    const minutes = pstDate.getMinutes();
    const period = hours >= 12 ? 'P.M.' : 'A.M.';
    hours = hours % 12 || 12;
    const formattedDateTime = `${month}/${day}/${year} ${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
    return formattedDateTime;
  }
  return '';
};

export const showSummaryDetails = filter => {
  // Display Summary Details when team selected, org selected or when filters selected greater than 1
  if (filter && filter.length > 0) {
    return (
      filter.some(
        f =>
          f.includes('team') || (f.includes('organization') && SS_FILTER_ACCESS.includes(parseInt(f.split('|')[1], 10)))
      ) || filter.length > 1
    );
  }
  return false;
};

export const showSummaryDetailsMA = filter => {
  // Display Market Details when Org selected or when filters selected greater than 1
  if (filter && filter.length > 0) {
    return (
      filter.some(f => f.includes('organization') && SS_FILTER_ACCESS.includes(parseInt(f.split('|')[1], 10))) ||
      (filter.length > 1 && !filter.some(f => f.includes('team')))
    );
  }
  return false;
};

export const summaryCalculation = summary => {
  if (summary && Object.values(summary).length > 0) {
    const totals = Object.values(summary).reduce(
      (acc, { current_tickets: currentTickets, initial_tickets: initialTickets }) => {
        acc.current += currentTickets;
        acc.start += initialTickets;
        return acc;
      },
      { current: 0, start: 0 }
    );
    return totals;
  }
  return { current: 0, start: 0 };
};

export const detailsCalculation = details => {
  if (details && Object.keys(details).length > 0) {
    // Summaries for users and managers
    const summaries = details.reduce(
      (acc, item) => {
        // Summary by pricing analyst
        if (item.pricing_analyst) {
          if (!acc.pricers[item.pricing_analyst]) {
            acc.pricers[item.pricing_analyst] = { current: 0, start: 0 };
          }
          acc.pricers[item.pricing_analyst].start += item.available_count_start;
          acc.pricers[item.pricing_analyst].current += item.number_of_tickets_current;
        }
        // Summary by market analyst
        if (item.market_analyst) {
          if (!acc.markets[item.market_analyst]) {
            acc.markets[item.market_analyst] = { current: 0, start: 0 };
          }
          acc.markets[item.market_analyst].start += item.available_count_start;
          acc.markets[item.market_analyst].current += item.number_of_tickets_current;
        }

        return acc;
      },
      { pricers: {}, markets: {} }
    );
    return summaries;
  }
  return { pricers: {}, markets: {} };
};
